import { lazy, type ReactElement } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router';

import type { ModuleWeb } from '../registry.js';

/**
 * Écrans du module Logistique (spec 010), chargés à la demande : espace bénévole (« Mes
 * emprunts », « Emprunter », « Contrôler un lot », « Véhicules ») et espace gestion (À traiter,
 * inventaire : lots, fiche lot, pharmacie, modèles de lots ; historique, alertes, catalogue,
 * flotte, contrôles techniques,
 * emprunts, réservations, tableau de bord, configuration, reprise et bascule).
 */
const page = <T extends string>(
  load: () => Promise<Record<T, (props: never) => ReactElement>>,
  name: T,
) => lazy(() => load().then((m) => ({ default: m[name] as () => ReactElement })));

const benevole = () => import('./BenevolePages.js');
const inventaire = () => import('./InventoryPages.js');
const flotte = () => import('./FleetPages.js');
const gestion = () => import('./GestionPages.js');
const lots = () => import('./LotsPages.js');

const MesEmpruntsPage = page(benevole, 'MesEmpruntsPage');
const EmprunterPage = page(benevole, 'EmprunterPage');
const MesLotsPage = page(benevole, 'MesLotsPage');
const ControlePage = page(benevole, 'ControlePage');
const MesVehiculesPage = page(benevole, 'MesVehiculesPage');
const ATraiterPage = page(inventaire, 'ATraiterPage');
const InventairePage = page(lots, 'InventairePage');
const LotPage = page(lots, 'LotPage');
const PharmaciePage = page(lots, 'PharmaciePage');
const ModelesPage = page(lots, 'ModelesPage');
const ModelePage = page(lots, 'ModelePage');

/** Ancienne adresse « Lots et stocks » (liens enregistrés, recherche) : vers l'inventaire. */
function AncienStocks(): ReactElement {
  const [params] = useSearchParams();
  const lot = params.get('ressource');
  return (
    <Navigate replace to={lot === null ? '/logistique/inventaire' : `/logistique/lots/${lot}`} />
  );
}
const HistoriquePage = page(inventaire, 'HistoriquePage');
const AlertesPage = page(inventaire, 'AlertesPage');
const CataloguePage = page(inventaire, 'CataloguePage');
const ConfigurationPage = page(inventaire, 'ConfigurationPage');
const ReassortPage = lazy(() =>
  inventaire().then((m) => ({
    default: function Reassort(): ReactElement {
      const { checkId = '' } = useParams();
      return <m.ReassortPage checkId={checkId} />;
    },
  })),
);
const FlottePage = page(flotte, 'FlottePage');
const VehiculePage = page(flotte, 'VehiculePage');
const ControlesTechniquesPage = page(flotte, 'ControlesTechniquesPage');
const EmpruntsPage = page(gestion, 'EmpruntsPage');
const ReservationsPage = page(gestion, 'ReservationsPage');
const PilotagePage = page(gestion, 'PilotagePage');
const ReprisePage = page(gestion, 'ReprisePage');

export const logistiqueWeb: ModuleWeb = {
  module: 'logistique',
  routes: [
    { path: '/logistique/mes-emprunts', element: <MesEmpruntsPage /> },
    { path: '/logistique/emprunter', element: <EmprunterPage /> },
    { path: '/logistique/mes-lots', element: <MesLotsPage /> },
    { path: '/logistique/controles/:checkId', element: <ControlePage /> },
    { path: '/logistique/mes-vehicules', element: <MesVehiculesPage /> },
    { path: '/logistique', element: <ATraiterPage /> },
    { path: '/logistique/reassort/:checkId', element: <ReassortPage /> },
    { path: '/logistique/inventaire', element: <InventairePage /> },
    { path: '/logistique/lots/:resourceId', element: <LotPage /> },
    { path: '/logistique/pharmacie', element: <PharmaciePage /> },
    { path: '/logistique/modeles', element: <ModelesPage /> },
    { path: '/logistique/modeles/:modelId', element: <ModelePage /> },
    { path: '/logistique/stocks', element: <AncienStocks /> },
    { path: '/logistique/historique', element: <HistoriquePage /> },
    { path: '/logistique/alertes', element: <AlertesPage /> },
    { path: '/logistique/catalogue', element: <CataloguePage /> },
    { path: '/logistique/configuration', element: <ConfigurationPage /> },
    { path: '/logistique/flotte', element: <FlottePage /> },
    { path: '/logistique/flotte/:vehicleId', element: <VehiculePage /> },
    { path: '/logistique/controles-techniques', element: <ControlesTechniquesPage /> },
    { path: '/logistique/emprunts', element: <EmpruntsPage /> },
    { path: '/logistique/reservations', element: <ReservationsPage /> },
    { path: '/logistique/pilotage', element: <PilotagePage /> },
    { path: '/logistique/reprise', element: <ReprisePage /> },
  ],
};
